import { Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { getDeepCopyOfObject } from 'app/shared/utilities/utility-methods.utility';

import { EvaluationInterface } from 'app/assessment/evaluation/models';
import {
  AssessmentAnswerInterface,
  AssessmentInterface,
  AssessmentPageInterface,
  AssessmentQuestionGroupInterface,
  AssessmentQuestionInterface,
} from 'app/assessment/models';
import { SurveyInterface } from 'app/assessment/survey/models';
import { CreateEntityForm } from 'app/assessment/utilities/create-entity-form';

@Injectable()
export class UpdateEntity {
  constructor(private _createEntityForm: CreateEntityForm) {}

  public updateSurvey(survey: SurveyInterface, step: number, form: FormGroup, complete: boolean): SurveyInterface {
    // taking a deep copy of the survey so we can change values (since the one passed in is readonly)
    const surveyCopy: SurveyInterface = getDeepCopyOfObject(survey);

    // step doesn't update on the last step via the mat-stepper
    const lastCompletedPage: number = complete ? surveyCopy.pages.length : step;
    if (lastCompletedPage > survey.lastCompletedPage) {
      surveyCopy.lastCompletedPage = lastCompletedPage;
    }

    const stepIndex: number = lastCompletedPage > 0 ? lastCompletedPage - 1 : 0;
    const currentSurveyPage: AssessmentPageInterface = surveyCopy.pages[stepIndex];
    const formAnswers: {} = form.value.formArray[stepIndex];

    surveyCopy.pages[stepIndex] = this.loopThroughUserAnswers(formAnswers, currentSurveyPage, false);
    surveyCopy.isComplete = complete;

    return surveyCopy;
  }

  public updateEvaluation(evaluation: EvaluationInterface, form: FormGroup): EvaluationInterface {
    // taking a deep copy of the entity so we can change values (since the one passed in is readonly)
    const evaluationCopy: EvaluationInterface = getDeepCopyOfObject(evaluation);

    // loop through every page to update all answers
    for (const key in evaluationCopy.pages) {
      if (!evaluationCopy.pages.hasOwnProperty(key)) { continue; }

      const i: number = Object.keys(evaluationCopy.pages).indexOf(key);
      const formAnswers: {} = form.value.formArray[i];

      evaluationCopy.pages[key] = this.loopThroughUserAnswers(formAnswers, evaluationCopy.pages[key], false);
    }

    return evaluationCopy;
  }

  public resetEntity(entity: AssessmentInterface): AssessmentInterface {
    // taking a deep copy of the entity so we can change values (since the one passed in is readonly)
    const entityCopy: SurveyInterface = getDeepCopyOfObject(entity);
    const formArray: FormArray = this._createEntityForm.createFormArray(entityCopy.pages);
    const formAnswers: {} = formArray.value;

    if (entityCopy.lastCompletedPage) {
      entityCopy.lastCompletedPage = 0;
    }

    return this.loopThroughPages(entityCopy, formAnswers);
  }

  private loopThroughPages(entity: SurveyInterface, formAnswers: {}): SurveyInterface {
    const entityCopy: SurveyInterface = entity;

    for(const formKey in formAnswers) {
      if (!formAnswers.hasOwnProperty(formKey)) { continue; }

      for (const key in entity.pages) {
        if (!entity.pages.hasOwnProperty(key)) { continue; }

        if (formKey === key) {
          entity.pages[key] = this.loopThroughUserAnswers(formAnswers[formKey], entity.pages[key], true);
        }
      }
    }

    return entityCopy;
  }

  // for each user answer, loop through entity to map the answer to it. then update the entity.
  private loopThroughUserAnswers(
    formAnswers: {},
    currentEntityPage: AssessmentPageInterface,
    reset: boolean,
  ): AssessmentPageInterface {
    // QUESTION GROUPS LOOP
    for (const userAnswerQuestionGroupId in formAnswers) {
      if (!formAnswers.hasOwnProperty(userAnswerQuestionGroupId)) { continue; }
      const userQuestionGroupAnswers: {} = formAnswers[userAnswerQuestionGroupId];
      let userAnswerQuestionId: number;
      let userAnswerFollowUpQuestionId: string;

      // QUESTION LOOP
      for (const key in userQuestionGroupAnswers) {
        if (!userQuestionGroupAnswers.hasOwnProperty(key)) { continue; }
        userAnswerQuestionId = 0;
        userAnswerFollowUpQuestionId = null;
        const userAnswerAnswer: AssessmentAnswerInterface = userQuestionGroupAnswers[key]; // THIS IS THE USER'S ANSWER FOR A QUESTION

        // if a dash is present in the key, it's a follow-up question / childQuestion
        if (key && /[-]/.test(key)) {
          userAnswerFollowUpQuestionId = key;
        } else {
          userAnswerQuestionId = Number(key);
        }

        ////////////////////////////////////////////////
        // LOOP THROUGH SURVEY TO MAP USER ANSWERS TO //
        ////////////////////////////////////////////////
        const qGroups: AssessmentQuestionGroupInterface[] = currentEntityPage.questionGroups;
        this.loopThroughQuestionGroups(
          qGroups,
          userAnswerQuestionGroupId,
          userAnswerQuestionId,
          userAnswerAnswer,
          userAnswerFollowUpQuestionId,
          reset,
        );
      }
    }

    return currentEntityPage;
  }

  private loopThroughQuestionGroups(
    qGroups: AssessmentQuestionGroupInterface[],
    userAnswerQuestionGroupId: string,
    userAnswerQuestionId: number,
    userAnswerAnswer: AssessmentAnswerInterface,
    userAnswerFollowUpQuestionId: string,
    reset: boolean,
  ): void {
    for (const group in qGroups) {
      if (!qGroups.hasOwnProperty(group)) { continue; }

      if (qGroups[group].id !== userAnswerQuestionGroupId) { continue; }
      const questions: AssessmentQuestionInterface[] = qGroups[group].questions;

      this.loopThroughQuestions(
        questions,
        userAnswerQuestionId,
        userAnswerAnswer,
        userAnswerFollowUpQuestionId,
        reset,
      );
    }
  }

  private loopThroughQuestions(
    questions: AssessmentQuestionInterface[],
    userAnswerQuestionId: number,
    userAnswerAnswer: AssessmentAnswerInterface,
    userAnswerFollowUpQuestionId: string,
    reset: boolean,
  ): void {
    for (const questionKey in questions) {
      if (!questions.hasOwnProperty(questionKey)) { continue; }

      // if user answer is part of a follow-up question AND current entity question has follow-up questions
      if (userAnswerFollowUpQuestionId && questions[questionKey].childQuestions.length > 0) {
        if (questions[questionKey].id !== Number(userAnswerFollowUpQuestionId.substr(0, userAnswerFollowUpQuestionId.indexOf('-')))) {
          continue;
        }

        const childQuestions: AssessmentQuestionInterface[] = questions[questionKey].childQuestions;

        this.loopThroughFollowUpQuestions(
          userAnswerAnswer,
          userAnswerFollowUpQuestionId,
          childQuestions,
          reset,
        );
        continue;
      }

      if (questions[questionKey].id !== userAnswerQuestionId) { continue; }
      const answers: AssessmentAnswerInterface[] = questions[questionKey].answers;

      if (!questions[questionKey].isMultipleChoice) {
        this.loopThroughAnswers(
          answers,
          userAnswerAnswer,
          reset,
        );
        continue;
      }

      this.loopThroughMultiChoiceAnswers(
        answers,
        userAnswerAnswer,
        reset,
      );
    }
  }

  private loopThroughFollowUpQuestions(
    userAnswerAnswer: AssessmentAnswerInterface,
    userAnswerFollowUpQuestionId: string,
    childQuestions: AssessmentQuestionInterface[],
    reset: boolean,
  ): void {
    for (const childQuestionKey in childQuestions) {
      if (!childQuestions.hasOwnProperty(childQuestionKey)) { continue; }
      const answers: AssessmentAnswerInterface[] = childQuestions[childQuestionKey].answers;

      if (childQuestions[childQuestionKey].id !==
        Number(userAnswerFollowUpQuestionId.substr(userAnswerFollowUpQuestionId.indexOf('-')+1))) { continue; }

        // if (typeof userAnswerAnswer === 'object') {
      if (!childQuestions[childQuestionKey].isMultipleChoice) {
        this.loopThroughAnswers(
          answers,
          userAnswerAnswer,
          reset,
        );
        continue;
      }

      this.loopThroughMultiChoiceAnswers(
        answers,
        userAnswerAnswer,
        reset,
      );
    }
  }

  private loopThroughAnswers(
    answers: AssessmentAnswerInterface[],
    userAnswerAnswer: AssessmentAnswerInterface,
    reset: boolean,
  ): void {
    for (const answerKey in answers) {
      if (!answers.hasOwnProperty(answerKey)) { continue; }

      if (reset) {
        const resetAnswer: AssessmentAnswerInterface = answers[answerKey];
        resetAnswer.isSelected = false;
        if (resetAnswer.text) {
          resetAnswer.text = '';
        }
        continue;
      }

      if (answers[answerKey].id === userAnswerAnswer.id) {
        const selectedAnswer: AssessmentAnswerInterface = answers[answerKey];
        selectedAnswer.isSelected = true;
        if (userAnswerAnswer.text) {
          selectedAnswer.text = userAnswerAnswer.text;
        }
        continue;
      }

      answers[answerKey].isSelected = false;
    }
  }

  private loopThroughMultiChoiceAnswers(
    answers: AssessmentAnswerInterface[],
    answer: any,
    reset: boolean,
  ): void {
    Object.keys(answer).map((key: string) => {
      const value: boolean | AssessmentAnswerInterface = answer[key];

      for (const answerKey in answers) {
        if (!answers.hasOwnProperty(answerKey)) { continue; }

        if (reset) {
          const selectedAnswer: AssessmentAnswerInterface = answers[answerKey];
          selectedAnswer.isSelected = false;
          selectedAnswer.text = null;
          continue;
        }

        if (answers[answerKey].id === Number(key)) {
          const selectedAnswer: AssessmentAnswerInterface = answers[answerKey];
          if (typeof value === 'object') {
            selectedAnswer.isSelected = true;
            selectedAnswer.text = value.text;
            continue;
          }
          selectedAnswer.isSelected = value;
          selectedAnswer.text = null;
        }
      }
    });
  }
}
